// Tenant/SubscriptionGuard.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { 
  FiAlertCircle, 
  FiClock, 
  FiMail, 
  FiShield,
  FiCreditCard,
  FiLogOut
} from 'react-icons/fi';
import logoutManager from '../logout';

const SubscriptionGuard = ({ 
  subscription, 
  isLoading, 
  children,
  showWarning = true,
  userRole = 'tenant_admin'
}) => {
  // Handle logout
  const handleLogout = async () => {
    try {
      await logoutManager.logout({
        redirectTo: '/',
        showToast: true,
        callbacks: false,
        onSuccess: () => {
          console.log('✅ Logged out successfully from subscription guard');
        }
      });
    } catch (error) {
      console.error('❌ Logout error:', error);
      // Force logout as fallback
      logoutManager.forceLogout('/');
    }
  };

  // If loading, show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking subscription status...</p>
        </div>
      </div>
    );
  }

  // ✅ Check if user is Tenant Admin or Super Admin
  const isTenantAdmin = userRole === 'tenant_admin' || 
                        userRole === 'super_admin' || 
                        subscription?.isTenantAdmin === true;

  // ✅ Check subscription status
  const hasActivePlan = subscription?.hasActivePlan || subscription?.isActive || false;
  const status = subscription?.status || 'none';
  const planName = subscription?.planName || 'No Plan';
  const daysRemaining = subscription?.daysRemaining || 0;
  const isExpiringSoon = subscription?.isExpiringSoon || false;

  console.log('🔍 [SubscriptionGuard] Check:', {
    userRole,
    isTenantAdmin,
    hasActivePlan,
    status,
    planName,
    daysRemaining,
    isExpiringSoon
  });

  // ✅ ============================================
  // ✅ TENANT ADMIN - Block dashboard if no subscription
  // ✅ ============================================
  if (isTenantAdmin) {
    // ✅ If NO active subscription - Show full page block with logout
    if (!hasActivePlan) {
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="min-h-screen flex items-center justify-center p-4 md:p-6 bg-gradient-to-br from-slate-50 to-slate-100"
        >
          <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-8 border-b border-gray-200">
              <div className="flex items-center justify-center mb-4">
                <div className="p-4 bg-amber-100 rounded-full">
                  <FiAlertCircle className="h-12 w-12 text-amber-600" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-center text-gray-900">
                No Active Subscription
              </h2>
              <p className="text-center text-gray-600 mt-2">
                Your institution does not have an active subscription plan
              </p>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">Subscription Status</span>
                  <span className="px-3 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                    {status || 'Inactive'}
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-sm text-gray-500">Current Plan</span>
                  <span className="text-sm font-medium text-gray-900">{planName}</span>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => window.location.href = '/tenant-admin/subscription'}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl transition-colors"
                >
                  <FiCreditCard className="h-5 w-5" />
                  Manage Subscription
                </button>
                
                <button
                  onClick={() => window.location.href = '/contact-support'}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded-xl transition-colors"
                >
                  <FiMail className="h-5 w-5" />
                  Contact Support
                </button>

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-red-300 hover:bg-red-50 text-red-600 font-medium rounded-xl transition-colors"
                >
                  <FiLogOut className="h-5 w-5" />
                  Sign Out
                </button>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <FiShield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-800">
                    <strong>Need help?</strong> Contact your Truyon administrator 
                    to activate a subscription plan for your institution.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      );
    }

    // ✅ If expiring soon (within 7 days) - Show warning but allow access
    if (showWarning && isExpiringSoon && daysRemaining > 0 && daysRemaining <= 7) {
      return (
        <>
          {/* Warning Banner */}
          <div className="bg-amber-50 border-b-4 border-amber-500 px-6 py-4">
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <FiClock className="h-5 w-5 text-amber-600 flex-shrink-0" />
                <p className="text-sm text-amber-800">
                  <span className="font-semibold">⚠️ Subscription Expiring Soon!</span>
                  {' '}Your subscription will expire in <span className="font-bold">{daysRemaining}</span> days.
                </p>
              </div>
              <button
                onClick={() => window.location.href = '/tenant-admin/subscription'}
                className="px-4 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
              >
                Renew Now
              </button>
            </div>
          </div>
          {children}
        </>
      );
    }

    // ✅ Active subscription - render children
    return children;
  }

  // ✅ ============================================
  // ✅ NON-ADMIN USERS - Block access if no subscription
  // ✅ ============================================
  
  // ❌ No active plan - show restriction (full page block with logout)
  if (!hasActivePlan) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="min-h-screen flex items-center justify-center p-4 md:p-6 bg-gradient-to-br from-slate-50 to-slate-100"
      >
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-8 border-b border-gray-200">
            <div className="flex items-center justify-center mb-4">
              <div className="p-4 bg-amber-100 rounded-full">
                <FiAlertCircle className="h-12 w-12 text-amber-600" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-center text-gray-900">
              Subscription Required
            </h2>
            <p className="text-center text-gray-600 mt-2">
              Your institution does not have an active subscription plan
            </p>
          </div>

          <div className="p-6 space-y-6">
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">Subscription Status</span>
                <span className="px-3 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                  {status || 'Inactive'}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-sm text-gray-500">Current Plan</span>
                <span className="text-sm font-medium text-gray-900">{planName}</span>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => window.location.href = '/contact-support'}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl transition-colors"
              >
                <FiMail className="h-5 w-5" />
                Contact Administrator
              </button>

              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-red-300 hover:bg-red-50 text-red-600 font-medium rounded-xl transition-colors"
              >
                <FiLogOut className="h-5 w-5" />
                Sign Out
              </button>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <FiShield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">
                  <strong>Need help?</strong> Contact your institution administrator 
                  to activate a subscription plan.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // ⚠️ Expiring soon for non-admin (show warning but allow access)
  if (showWarning && isExpiringSoon && daysRemaining > 0 && daysRemaining <= 7) {
    return (
      <>
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-3">
          <div className="max-w-7xl mx-auto flex items-center gap-3">
            <FiClock className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              <span className="font-semibold">Subscription Expiring Soon!</span>
              {' '}Your subscription will expire in <span className="font-bold">{daysRemaining}</span> days. 
              {' '}<span className="text-amber-700">Please contact your administrator.</span>
            </p>
          </div>
        </div>
        {children}
      </>
    );
  }

  // ✅ Active subscription - render children
  return children;
};

export default SubscriptionGuard;